import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { AppIconComponent } from '../brand/app-icon.component';
import { IconComponent } from '../icon/icon.component';
import { IconName } from '../icon/icons';

export type NavItemVariant = 'sidebar' | 'bottom' | 'site';

/**
 * Voce di navigazione. La voce attiva ha il fondo `bg-bsite-100` e `aria-current="page"`
 * (FR-024). Variante `bottom` per la barra in basso (area di tocco di almeno 56 px), `site` per
 * l'elenco dei cantieri della barra laterale. Senza `link` è un pulsante (per esempio "Altro").
 */
@Component({
  selector: 'bs-nav-item',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, RouterLink, RouterLinkActive, IconComponent, AppIconComponent],
  host: { class: 'block' },
  template: `
    <ng-template #content let-isActive>
      @if (variant() === 'bottom') {
        <span
          [class]="
            'inline-flex h-8 w-14 items-center justify-center rounded-full ' +
            (isActive ? 'bg-bsite-100 text-ink dark:bg-bsite-400' : '')
          "
        >
          @if (icon(); as name) {
            <bs-icon [name]="name" [size]="20" />
          }
        </span>
        <span class="max-w-full truncate">{{ label() }}</span>
      } @else {
        @if (variant() === 'site') {
          <span
            [class]="
              'size-2 shrink-0 rounded-full ' +
              (isActive ? 'bg-bsite-400' : 'bg-stone-400 dark:bg-stone-500')
            "
            aria-hidden="true"
          ></span>
        } @else if (appCode(); as code) {
          <bs-app-icon [code]="code" size="sm" [muted]="appMuted()" />
        } @else if (icon(); as name) {
          <bs-icon [name]="name" [size]="20" />
        }
        <span class="min-w-0 flex-1 truncate">{{ label() }}</span>
        @if (badge(); as count) {
          <span
            class="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-ink px-1.5 text-xs font-bold text-white dark:bg-stone-50 dark:text-ink"
          >
            {{ count }}<span class="sr-only"> da vedere</span>
          </span>
        }
      }
    </ng-template>

    @if (link(); as target) {
      <a
        [routerLink]="target"
        routerLinkActive
        #active="routerLinkActive"
        [routerLinkActiveOptions]="{ exact: exact() }"
        ariaCurrentWhenActive="page"
        [class]="active.isActive ? activeClass() : idleClass()"
        [attr.title]="label()"
        [attr.data-testid]="testId()"
        [attr.data-active]="active.isActive ? 'true' : null"
      >
        <ng-container
          [ngTemplateOutlet]="content"
          [ngTemplateOutletContext]="{ $implicit: active.isActive }"
        />
      </a>
    } @else {
      <button
        type="button"
        [class]="idleClass() + ' w-full'"
        [attr.aria-expanded]="expanded()"
        [attr.aria-controls]="controls()"
        [attr.data-testid]="testId()"
        (click)="pressed.emit()"
      >
        <ng-container
          [ngTemplateOutlet]="content"
          [ngTemplateOutletContext]="{ $implicit: false }"
        />
      </button>
    }
  `,
})
export class NavItemComponent {
  readonly label = input.required<string>();
  readonly link = input<string | readonly unknown[] | null>(null);
  readonly icon = input<IconName | null>(null);
  /** Codice del modulo: al posto dell'icona compare la tessera del modulo. */
  readonly appCode = input<string | null>(null);
  readonly appMuted = input(false);
  readonly exact = input(false);
  readonly badge = input<number | null>(null);
  readonly variant = input<NavItemVariant>('sidebar');
  readonly expanded = input<boolean | null>(null);
  readonly controls = input<string | null>(null);
  readonly testId = input<string | null>(null);
  /** Solo senza `link`: la voce è stata scelta. */
  readonly pressed = output();

  protected readonly idleClass = computed(() => this.classes(false));
  protected readonly activeClass = computed(() => this.classes(true));

  private classes(active: boolean): string {
    switch (this.variant()) {
      case 'bottom':
        return `flex h-16 min-w-14 flex-col items-center justify-center gap-1 px-1 text-xs ${
          active
            ? 'font-semibold text-ink dark:text-stone-50'
            : 'font-medium text-stone-600 dark:text-stone-300'
        }`;
      case 'site':
        return `flex h-11 items-center gap-2.5 rounded-full px-3 text-sm lg:h-9 ${
          active
            ? 'bg-stone-200 font-semibold text-ink dark:bg-stone-800 dark:text-stone-50'
            : 'font-medium text-stone-600 hover:bg-stone-200 hover:text-ink dark:text-stone-300 dark:hover:bg-stone-800 dark:hover:text-stone-50'
        }`;
      default:
        return `flex h-11 items-center gap-3 rounded-full px-3 text-sm lg:h-10 ${
          active
            ? 'bg-bsite-100 font-semibold text-ink dark:bg-bsite-400 dark:text-ink'
            : 'font-medium text-stone-600 hover:bg-stone-200 hover:text-ink dark:text-stone-300 dark:hover:bg-stone-800 dark:hover:text-stone-50'
        }`;
    }
  }
}
